import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { useJournal } from '../../contexts/JournalContext';
import { useEncryption } from '../../contexts/EncryptionContext';

interface Memory {
  id: string;
  date: string;
  preview: string;
}

const DashboardPage = () => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [moodStats, setMoodStats] = useState<any>(null);
  const { entries } = useJournal();
  const { decrypt } = useEncryption();
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    const loadMemories = async () => {
      const today = new Date();
      const memories = await Promise.all(
        entries
          .filter(entry => {
            const entryDate = new Date(entry.entry_date);
            return (
              entryDate.getDate() === today.getDate() &&
              entryDate.getMonth() === today.getMonth() &&
              entryDate.getFullYear() < today.getFullYear()
            );
          })
          .map(async entry => {
            const decryptedContent = await decrypt(entry.encrypted_content);
            return {
              id: entry.id,
              date: new Date(entry.entry_date).toLocaleDateString(),
              preview: decryptedContent.substring(0, 150) + '...',
            };
          })
      );
      setMemories(memories);
    };

    const calculateMoodStats = async () => {
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      
      const recentEntries = entries.filter(
        entry => new Date(entry.entry_date) >= lastWeek
      );

      const moodCounts: { [key: string]: number } = {};
      for (const entry of recentEntries) {
        if (entry.encrypted_mood_label) {
          const mood = await decrypt(entry.encrypted_mood_label);
          moodCounts[mood] = (moodCounts[mood] || 0) + 1;
        }
      }

      const sortedMoods = Object.entries(moodCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);

      setMoodStats(sortedMoods);
    };

    loadMemories();
    calculateMoodStats();
  }, [entries, decrypt]);

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg">Dashboard</Heading>

        {memories.length > 0 && (
          <Box>
            <Heading size="md" mb={4}>
              On This Day...
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {memories.map(memory => (
                <Card key={memory.id} bg={cardBg}>
                  <CardHeader>
                    <Text fontWeight="bold">{memory.date}</Text>
                  </CardHeader>
                  <CardBody>
                    <Text>{memory.preview}</Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {moodStats && (
          <Box>
            <Heading size="md" mb={4}>
              Your Week in Moods
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {moodStats.map(([mood, count]: [string, number]) => (
                <Card key={mood} bg={cardBg}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Mood</StatLabel>
                      <StatNumber>{mood}</StatNumber>
                      <StatHelpText>{count} entries</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default DashboardPage; 